import { createSelector } from '@reduxjs/toolkit';
import {
    get,
    groupBy,
    identity,
    isEmpty,
    isNil,
    pipe,
    size,
    sumBy,
    uniq,
    zipObject,
    zipAll
} from 'lodash/fp';
import { QueryState, PrimaryDimensions, DateBin } from 'app/lib/constants';
import { getDateRangeFromSelectedBars } from 'app/lib/utils';
import { transformDataset } from 'app/store/transformers.js';
import { rowsToColumns } from 'app/store/snowflakeQueries/utils.js';
import { QueryKeys } from 'app/store/snowflakeQueries/reducer.js';
import {
    selectQueryForPropKey,
    selectQueryMetadataForPropKey,
    selectQueryResultsForPropKey
} from 'app/store/snowflakeQueries/selectors.js';
import {
    calcBinFromDateRange,
    getTwelveMonths,
    calcTrend,
    getLastTwoFullMonths,
    generateEmptyColumns,
    generateMeasure,
    generateDimensions
} from './utils.js';

export const ACCOUNT_LEVEL = '<ACCOUNT-LEVEL>';

const selectSlice = get('usageReporting');

export const selectSpendOverviewSelectedWarehouses = createSelector(
    selectSlice,
    get('spendOverview.selectedWarehouses')
);

export const selectSpendOverviewWarehouseFilter = createSelector(
    selectSlice,
    get('spendOverview.warehouseFilter')
);

const selectIsAccountLevel = createSelector(
    selectSpendOverviewSelectedWarehouses,
    selectSpendOverviewWarehouseFilter,
    (selectedWarehouses, warehouseFilter) => isEmpty(selectedWarehouses) && isNil(warehouseFilter)
);

export const selectQueryHistorySchema = createSelector(selectSlice, get('queryHistoryColumns'));

export const selectSelectedChartArea = createSelector(
    selectSlice,
    get('warehouseCube.selectedChartArea')
);

export const selectChartType = createSelector(selectSlice, get('warehouseCube.chartType'));

const selectWarehouseUsageQuery = createSelector(identity, (state) =>
    selectQueryForPropKey(state, { queryKey: QueryKeys.USAGE_REPORTING_WAREHOUSE_USAGE })
);

const selectWarehousesQuery = createSelector(identity, (state) =>
    selectQueryForPropKey(state, { queryKey: QueryKeys.USAGE_REPORTING_WAREHOUSES })
);

export const selectWarehouseUsageMetadata = createSelector(identity, (state) =>
    selectQueryMetadataForPropKey(state, {
        queryKey: QueryKeys.USAGE_REPORTING_WAREHOUSE_USAGE
    })
);

export const selectIsLoadingWarehouseUsage = createSelector(
    selectWarehouseUsageQuery,
    (query) => query.state === QueryState.RUNNING
);

const selectSpendOverviewFilteredRecords = createSelector(
    selectWarehouseUsageQuery,
    selectSpendOverviewWarehouseFilter,
    (query, warehouseFilter) => {
        if (query.state !== QueryState.SUCCESS) {
            return null;
        }

        return query.data.records.filter((record) =>
            isNil(warehouseFilter)
                ? record[0] !== ACCOUNT_LEVEL
                : warehouseFilter.includes(record[0])
        );
    }
);

const selectSpendOverviewAccountLevelRecords = createSelector(
    selectWarehouseUsageQuery,
    (query) => {
        if (query.state !== QueryState.SUCCESS) {
            return null;
        }

        return query.data.records.filter((record) => record[0] === ACCOUNT_LEVEL);
    }
);

export const selectWarehouseUsageYTD = createSelector(
    selectSpendOverviewFilteredRecords,
    (filteredRecords) => {
        if (isNil(filteredRecords)) {
            return null;
        }

        const byWarehouse = groupBy('0', filteredRecords);

        const rollup = Object.keys(byWarehouse).map((name) => {
            const rows = byWarehouse[name];
            const total = Math.round(sumBy('2', rows) * 100) / 100;
            const { firstMonth, secondMonth } = getLastTwoFullMonths(byWarehouse, name);

            const { diff, diffPercentage, direction } = calcTrend(firstMonth, secondMonth);

            return [
                name,
                total,
                {
                    diff,
                    diffPercentage,
                    direction
                }
            ];
        });

        const data = {
            columns: [
                { name: 'WAREHOUSE_NAME', typeFamily: 'STRING' },
                { name: 'YTD_SPEND', typeFamily: 'DOUBLE' },
                { name: 'TREND', typeFamily: 'ARRAY' }
            ],
            records: rollup,
            rowCount: rollup.length
        };

        return pipe(rowsToColumns, (d) => transformDataset(d, null))(data);
    }
);

export const selectWarehouseUsageWarehouses = createSelector(selectWarehouseUsageQuery, (query) => {
    if (query.state !== QueryState.SUCCESS) {
        return null;
    }

    return Object.keys(groupBy('0', query.data.records)).filter(
        (warehouse) => warehouse !== ACCOUNT_LEVEL
    );
});

export const selectSpendOverviewSelectedRowIndexes = createSelector(
    selectWarehouseUsageYTD,
    selectSpendOverviewSelectedWarehouses,
    (warehouseUsage, selectedWarehouses) => {
        if (isEmpty(selectedWarehouses)) {
            return null;
        }

        const warehouseUsageColumns = warehouseUsage?.columns[0];

        if (warehouseUsageColumns) {
            const rowIndexes = [];

            selectedWarehouses.forEach((warehouse) => {
                const index = warehouseUsageColumns?.findIndex((name) => name === warehouse);

                if (index !== -1) {
                    rowIndexes.push(index);
                }
            });

            return rowIndexes;
        }

        return null;
    }
);

const selectSpendOverviewTableRecords = createSelector(
    selectSpendOverviewFilteredRecords,
    selectSpendOverviewSelectedWarehouses,
    (filteredRecords, selectedWarehouses) => {
        if (isNil(filteredRecords)) {
            return null;
        }

        return !isEmpty(selectedWarehouses)
            ? filteredRecords.filter((row) => selectedWarehouses.includes(row[0]))
            : filteredRecords;
    }
);

export const selectTotalSpendTrendForSelectedWarehouses = createSelector(
    selectWarehouseUsageQuery,
    selectSpendOverviewTableRecords,
    (query, records) => {
        if (query.state !== QueryState.SUCCESS) {
            return null;
        }

        const byWarehouse = groupBy('0', records);

        const { firstMonth, secondMonth } = Object.keys(byWarehouse)
            .map((name) => getLastTwoFullMonths(byWarehouse, name))
            .reduce(
                (acc, { firstMonth, secondMonth }) => ({
                    firstMonth: acc.firstMonth + firstMonth,
                    secondMonth: acc.secondMonth + secondMonth
                }),
                { firstMonth: 0, secondMonth: 0 }
            );

        return calcTrend(firstMonth, secondMonth);
    }
);

export const selectMonthlyWarehousesUsage = createSelector(
    selectIsAccountLevel,
    selectSpendOverviewAccountLevelRecords,
    selectSpendOverviewTableRecords,
    (isAccountLevel, accountLevelRecords, warehouseRecords) => {
        if (isNil(warehouseRecords)) {
            return null;
        }

        const byMonth = groupBy('1', warehouseRecords);
        const records = getTwelveMonths().map((month) => {
            const rows = isAccountLevel
                ? accountLevelRecords.filter((record) => record[1] === month.toSQLDate())
                : byMonth[month.toSQLDate()];
            const total = rows ? Math.round(sumBy('2', rows) * 100) / 100 : 0;

            return [month.toSQLDate(), total];
        });

        const data = {
            columns: [
                { name: 'MONTH', type: 'TIMESTAMP', typeFamily: 'TIMESTAMP' },
                { name: 'SPEND', typeFamily: 'DOUBLE' }
            ],
            records,
            rowCount: records.length
        };

        return pipe(rowsToColumns, (d) => transformDataset(d, null))(data);
    }
);

export const selectTotalWarehousesUsage = createSelector(
    selectIsAccountLevel,
    selectSpendOverviewAccountLevelRecords,
    selectSpendOverviewTableRecords,
    (isAccountLevel, accountLevelRecords, warehouseRecords) => {
        if (isNil(warehouseRecords)) {
            return null;
        }

        return (
            Math.round(sumBy('2', isAccountLevel ? accountLevelRecords : warehouseRecords) * 100) /
            100
        );
    }
);

export const selectWarehouseDetails = createSelector(
    selectWarehousesQuery,
    selectSpendOverviewSelectedWarehouses,
    (query, selectedWarehouses) => {
        if (isEmpty(selectedWarehouses) || selectedWarehouses.length > 1 || isEmpty(query?.data)) {
            return null;
        }

        const keys = query.data.columns.map((column) => column.name);
        const values = query.data.records.find((row) => row[0] === selectedWarehouses[0]);

        if (isEmpty(values)) {
            return null;
        }

        return zipObject(keys, values);
    }
);

export const selectHasAccessToWarehouseUsageTable = createSelector(
    selectSlice,
    get('warehouseCube.hasAccessToWarehouseUsageTable')
);

const selectWarehouseCubeResults = createSelector(identity, (state) =>
    selectQueryResultsForPropKey(state, { queryKey: QueryKeys.USAGE_REPORTING_WAREHOUSE_CUBE })
);

export const selectWarehouseCubeResultsRowCount = createSelector(
    selectWarehouseCubeResults,
    (data) => data?.rowCount ?? null
);

export const selectDateTimeCardinality = createSelector(selectWarehouseCubeResults, (data) => {
    const dates = data?.columns[0]?.map((date) => date.valueOf());
    return size(uniq(dates));
});

export const selectDateDomainRange = createSelector(selectWarehouseCubeResults, (data) => {
    if (isEmpty(data?.columns)) {
        return null;
    }
    const dates = data.columns[0];
    const max = new Date(Math.max(...dates));
    const min = new Date(Math.min(...dates));
    return { min, max };
});

export const selectWarehouseCubeMetadata = createSelector(identity, (state) =>
    selectQueryMetadataForPropKey(state, { queryKey: QueryKeys.USAGE_REPORTING_WAREHOUSE_CUBE })
);

export const selectWarehouseCubeDateFilter = createSelector(
    selectSlice,
    get('warehouseCube.dateFilter')
);

export const selectWarehouseCubeWarehouses = createSelector(identity, (state) => {
    const results = selectQueryResultsForPropKey(state, {
        queryKey: QueryKeys.USAGE_REPORTING_WAREHOUSE_CUBE_WAREHOUSE_VALUES
    });

    return results?.columns[0];
});

export const selectWarehouseCubeUsers = createSelector(identity, (state) => {
    const results = selectQueryResultsForPropKey(state, {
        queryKey: QueryKeys.USAGE_REPORTING_WAREHOUSE_CUBE_USER_VALUES
    });

    return results?.columns[0];
});

export const selectIsLoadingWarehousesList = createSelector(identity, (state) => {
    const metadata = selectQueryMetadataForPropKey(state, {
        queryKey: QueryKeys.USAGE_REPORTING_WAREHOUSE_CUBE_WAREHOUSE_VALUES
    });
    return metadata.state === QueryState.RUNNING;
});

export const selectIsLoadingUsersList = createSelector(identity, (state) => {
    const metadata = selectQueryMetadataForPropKey(state, {
        queryKey: QueryKeys.USAGE_REPORTING_WAREHOUSE_CUBE_USER_VALUES
    });
    return metadata.state === QueryState.RUNNING;
});

export const selectWarehouseCubeWarehouseFilter = createSelector(
    selectSlice,
    get('warehouseCube.warehouseFilter')
);

export const selectWarehouseCubeUserFilter = createSelector(
    selectSlice,
    get('warehouseCube.userFilter')
);

export const selectWarehouseCubeSecondaryDimension = createSelector(
    selectSlice,
    get('warehouseCube.secondaryDimension')
);

export const selectWarehouseCubePrimaryDimension = createSelector(
    selectSlice,
    get('warehouseCube.primaryDimension')
);

export const selectWarehouseCubeSelectedMeasure = createSelector(
    selectSlice,
    get('warehouseCube.selectedMeasure')
);

export const selectWarehouseCubeDateBin = createSelector(selectSlice, get('warehouseCube.dateBin'));

export const selectWarehouseCubeCaluclatedDateBin = createSelector(
    selectWarehouseCubeDateBin,
    selectWarehouseCubeDateFilter,
    (dateBin, dateFilter) => {
        if (isNil(dateBin) || dateBin === DateBin.AUTO) {
            return calcBinFromDateRange(dateFilter);
        }

        return dateBin;
    }
);

const selectAvailableWarehouseCubeWarehouses = createSelector(
    selectWarehouseCubeWarehouses,
    selectWarehouseCubeWarehouseFilter,
    (warehouses, warehouseFilter) => (isEmpty(warehouseFilter) ? warehouses : warehouseFilter)
);

const selectAvailableWarehouseCubeUsers = createSelector(
    selectWarehouseCubeUsers,
    selectWarehouseCubeUserFilter,
    (users, userFilter) => (isEmpty(userFilter) ? users : userFilter)
);

const selectAvailableWarehouseCubeWarehousesAndUsers = createSelector(
    selectAvailableWarehouseCubeWarehouses,
    selectAvailableWarehouseCubeUsers,
    (warehouses, users) => ({ warehouses, users })
);

export const selectWarehouseCubeChartData = createSelector(
    selectWarehouseCubeCaluclatedDateBin,
    selectWarehouseCubeResults,
    selectAvailableWarehouseCubeWarehousesAndUsers,
    selectWarehouseCubeDateFilter,
    (calculatedDateBin, results, availableWarehousesAndUsers, dateFilter) => {
        if (isNil(results)) {
            return null;
        }

        const dimensions = generateDimensions(results, calculatedDateBin);
        const data =
            results.rowCount === 0
                ? generateEmptyColumns({ dimensions, dateFilter, ...availableWarehousesAndUsers })
                : zipAll(results.columns);

        return {
            data,
            dimensions,
            measures: generateMeasure(results)
        };
    }
);

export const selectCurrentTab = createSelector(selectSlice, get('selectedTab'));

const selectFilterFromSelectedArea = createSelector(
    selectSelectedChartArea,
    selectWarehouseCubePrimaryDimension,
    selectWarehouseCubeCaluclatedDateBin,
    (selection, dimension, dateBin) => {
        if (isEmpty(selection)) {
            return {};
        }
        if (dimension === PrimaryDimensions.USER) {
            return { users: selection };
        }
        if (dimension === PrimaryDimensions.WAREHOUSE) {
            return { warehouses: selection };
        }
        return getDateRangeFromSelectedBars(selection, dateBin);
    }
);

export const selectQueryDetailsInitialModel = createSelector(
    selectWarehouseCubeDateFilter,
    selectWarehouseCubeWarehouseFilter,
    selectWarehouseCubeUserFilter,
    selectFilterFromSelectedArea,
    (dateFilters, warehouseFilters, userFilters, chartFilter) => {
        const model = {
            filters: []
        };
        const { users, warehouses, rangeFrom, rangeTo } = chartFilter;
        if (rangeFrom || dateFilters?.rangeFrom) {
            model.filters.push({
                column: { name: 'START_TIME', type: 'TIMESTAMP' },
                operator: { name: '>=' },
                value: { name: rangeFrom ?? dateFilters?.rangeFrom, isLiteral: true }
            });
        }
        if (rangeTo || dateFilters?.rangeTo) {
            model.filters.push({
                column: { name: 'END_TIME', type: 'TIMESTAMP' },
                operator: { name: '<=' },
                value: { name: rangeTo ?? dateFilters?.rangeTo, isLiteral: true }
            });
        }
        if (!isEmpty(warehouses) || !isEmpty(warehouseFilters)) {
            model.filters.push({
                column: { name: 'WAREHOUSE_NAME', type: 'STRING' },
                operator: { name: 'in' },
                value: warehouses ?? warehouseFilters
            });
        }
        if (!isEmpty(users) || !isEmpty(userFilters)) {
            model.filters.push({
                column: { name: 'USER_NAME', type: 'STRING' },
                operator: { name: 'in' },
                value: users ?? userFilters
            });
        }
        return model;
    }
);

const isInSelectionRange = (selection, item, dimension) => {
    if (dimension !== PrimaryDimensions.DATE) {
        return selection.includes(item);
    }
    const rangeFrom = selection[0];
    const rangeTo = selection[1] ?? selection[0];
    return item.toMillis() >= rangeFrom.toMillis() && item.toMillis() <= rangeTo.toMillis();
};

export const selectSelectedBarMetadata = createSelector(
    selectWarehouseCubeResults,
    selectSelectedChartArea,
    selectWarehouseCubePrimaryDimension,
    (data, selection, dimension) => {
        if (isEmpty(data?.columns) || isEmpty(selection)) {
            return null;
        }

        const columnsArrayLength = data.columns.length;
        const values = data.columns[columnsArrayLength - 1];
        const hasSecondaryDimension = columnsArrayLength > 2;
        const secondaryDimensionValues = [];
        let total = 0;

        data.columns[0].forEach((column, i) => {
            if (isInSelectionRange(selection, column, dimension)) {
                total += values[i];
                if (hasSecondaryDimension) {
                    secondaryDimensionValues.push(data.columns[1][i]);
                }
            }
        });

        return { total, secondaryDimensionValues: uniq(secondaryDimensionValues) };
    }
);

export const selectSnowflakeHistoryJobDefinition = createSelector(
    selectSlice,
    get('snowflakeHistoryIndexingJob.jobDef')
);

export const selectUsageWarehouseJob = createSelector(selectSlice, get('warehouseJob.jobDef'));

export const selectUsageTableJob = createSelector(selectSlice, get('tcoJob.jobDef'));

export const selectHasUsageWarehouseJob = createSelector(
    selectUsageWarehouseJob,
    (job) => !isNil(job)
);
